import loadable from "@loadable/component"
import { Suspense, useEffect, useRef, useState } from "react"
import { Helmet } from "react-helmet-async"
import { useDispatch, useSelector } from "react-redux"

import { setExperiment } from "app-store-v2/app/actions"
import ThresholdProgressBar from "components/threshold-progress-bar"
import Confirmation from "components/ui/confirmation"
import Flashbar from "components/ui/flashbar"
import Media from "components/ui/media"
// import Shimmer from "components/ui/shimmer"
import { jewlr_t, isTestMode, retry, IS_JEWLR } from "helpers/application"
import { freeGiftPromoEnabled } from "helpers/holiday"
import useWindowDimensions from "helpers/window-dimensions"

import CartLoading from "./cart-loading"
import CartTitle from "./cart-title"
import { MainContainer, SidePanelWrapper } from "./components"
import EmptyCart from "./empty-cart"
import ExclusiveAddons from "./exclusive-addons"
import AddonLineItems from "./exclusive-addons/line-items"
import UpsellSection from "./exclusive-addons/upsell-section"
import Footer from "./footer"
import FreeGiftsDesktop from "./free-gifts/desktop"
import FreeGifts from "./free-gifts/mobile"
import fetchCart from "./hoc/fetch-cart"
import LineItem from "./line-item"
import OutOfStockBanner from "./out-of-stock-banner"
import PromoCode from "./promo-code"
import Promos from "./promos"
import SidePanel from "./side-panel"

const DetailModal = loadable(
  () => retry(() => import("./exclusive-addons/detail-modal")),
  { ssr: false }
)
const ErrorModal = loadable(
  () => retry(() => import("areas/checkout/checkout/modal/error-modal")),
  { ssr: false }
)
const GiftPreviewVideoModal = loadable(
  () => retry(() => import("./line-item/gift-preview-video")),
  { ssr: false }
)

const Cart = () => {
  const [onRemoveAction, setOnRemoveAction] = useState()
  const [showAddonModal, toggleAddonModal] = useState(false)
  const [addonInModal, setAddonInModal] = useState({})
  const [disableActions, toggleDisableActions] = useState({
    action: "",
    id: "",
  })
  const cartSummaryRef = useRef()

  const cartData = useSelector((state) => state.cart)
  const cartLoaded = cartData?.cartLoaded
  const couponApplied = cartData?.summary?.promo_code?.promo_code_applied
  const cartWarrantyExp = useSelector((state) => state.experiment.cartWarranty)
  const dispatch = useDispatch()

  const { isMobile } = useWindowDimensions()
  let content = null

  const UPSELLKIT_CODE = "FOAMKIT"
  const upsellKit = (cartData.premium_gift_offers || []).find(
    (item) => item.code === UPSELLKIT_CODE
  )
  const freeGiftPromoApplied = () =>
    !sl.config.free_gift_promo?.code ||
    couponApplied?.toUpperCase() ===
      sl.config.free_gift_promo.code?.toUpperCase()

  useEffect(() => {
    if (!isTestMode) {
      dataLayer.push({
        event: "viewed-cart",
      })
    }
  }, [])

  useEffect(() => {
    if (typeof window.Beacon !== "undefined") {
      window.Beacon("config", { hideFABOnMobile: true })
    }
    return () => {
      if (typeof window.Beacon !== "undefined") {
        window.Beacon("config", { hideFABOnMobile: false })
      }
    }
  }, [isMobile])

  useEffect(() => {
    if (cartWarrantyExp) {
      dispatch(
        setExperiment({
          cartWarranty: cartWarrantyExp,
        })
      )
    }
  }, [cartWarrantyExp])

  if (!cartLoaded) {
    content = (
      <>
        <div className="contained tablet:mb-12 tablet:pt-0 bg-grey-4 tablet:bg-white flex flex-col px-16 pt-10">
          {/* Promo */}
          {/* <Shimmer
            className="tablet:mt-32 tablet:mb-0 mb-30 w-full"
            style={{ height: "48px" }}
          /> */}
          <CartTitle cartData={cartData} />
        </div>
        <div className="contained tablet:mb-0 bg-grey-4 tablet:bg-white mb-16 px-16">
          <CartLoading />
        </div>
      </>
    )
  } else if (cartData.line_items.length == 0) {
    content = <EmptyCart />
  } else {
    content = (
      <>
        <div className="contained tablet:mb-12 tablet:pt-0 bg-grey-4 tablet:bg-white flex flex-col px-16 pt-10">
          {sl.config.sale_key.includes("2025_jewlr_days_flash") && (
            <Media className="mb-16 mt-32" greaterThanOrEqual="desktop">
              <ThresholdProgressBar />
            </Media>
          )}
          <Promos />
          {cartData.summary.has_out_of_stock_item && <OutOfStockBanner />}
          <CartTitle cartData={cartData} />
          {sl.config.sale_key.includes("2025_jewlr_days_flash") && (
            <Media className="tablet:mt-10" lessThan="desktop">
              <ThresholdProgressBar />
            </Media>
          )}
        </div>
        <div className="contained tablet:mb-0 bg-grey-4 tablet:bg-white mb-16 px-16">
          {cartData.err_msg && (
            <Flashbar message={cartData.err_msg} type="error" />
          )}
          <div className="desktop:flex-row tablet:gap-16 flex flex-col">
            <MainContainer>
              {freeGiftPromoEnabled() && (
                <>
                  {freeGiftPromoApplied() && <FreeGiftsDesktop />}
                  <div className="tablet:hidden -mt-20 mb-24">
                    <PromoCode promo />
                    {freeGiftPromoApplied() && <FreeGifts />}
                  </div>
                </>
              )}
              {cartData.line_items.map((line_item) => (
                <LineItem
                  item={line_item}
                  key={`cartItem-${line_item.id}`}
                  setOnRemoveAction={setOnRemoveAction}
                />
              ))}
              {/* <FreeGiftsDesktop />
              <FreeGifts /> */}
              <AddonLineItems setOnRemoveAction={setOnRemoveAction} />
              {IS_JEWLR() && upsellKit && (
                <UpsellSection
                  addon={upsellKit}
                  disableActions={disableActions}
                  toggleAddonModal={toggleAddonModal}
                  toggleDisableActions={toggleDisableActions}
                />
              )}
            </MainContainer>
            {IS_JEWLR() && (
              <Media className="-mx-16 mt-40 bg-white" lessThan="tablet">
                <ExclusiveAddons
                  disableActions={disableActions}
                  setAddonInModal={setAddonInModal}
                  toggleAddonModal={toggleAddonModal}
                  toggleDisableActions={toggleDisableActions}
                  upsellKit={UPSELLKIT_CODE}
                />
              </Media>
            )}
            <SidePanelWrapper>
              <SidePanel cartSummaryRef={cartSummaryRef} />
            </SidePanelWrapper>
          </div>
          {IS_JEWLR() && (
            <Media greaterThanOrEqual="tablet">
              <ExclusiveAddons
                disableActions={disableActions}
                setAddonInModal={setAddonInModal}
                toggleAddonModal={toggleAddonModal}
                toggleDisableActions={toggleDisableActions}
                upsellKit={UPSELLKIT_CODE}
              />
            </Media>
          )}
        </div>
        <Footer />
        {typeof window !== "undefined" && (
          <Suspense fallback={<div />}>
            {sl.config.enable_gift_preview_video && <GiftPreviewVideoModal />}
            <ErrorModal />
            <DetailModal
              addon={addonInModal}
              disableActions={disableActions}
              show={showAddonModal}
              toggleAddonModal={toggleAddonModal}
              toggleDisableActions={toggleDisableActions}
            />
            <Confirmation
              cancelText="Cancel"
              confirmText="Yes"
              description={`Are you sure you want to remove this item from your ${jewlr_t("Cart")}?`}
              onCancel={() => setOnRemoveAction(null)}
              onConfirm={() => {
                if (onRemoveAction) {
                  onRemoveAction()
                }
                setOnRemoveAction(null)
              }}
              show={!!onRemoveAction}
              title="Remove Item"
            />
          </Suspense>
        )}
      </>
    )
  }

  return (
    <>
      <Helmet>
        <meta content="noindex" name="robots" />
      </Helmet>
      {content}
    </>
  )
}

export default fetchCart(Cart)
